import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { BookOpen, MapPin, FileText, Search, Grid, List, Play, Download } from 'lucide-react';
import { quranApi } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { SectionHeader, PageLoading, AnimatedCard } from '../components/shared';
import { useAudio } from '../context/AudioContext';

const QuranList = () => {
  const { t } = useTranslation();
  const [surahs, setSurahs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState('all');
  const [viewMode, setViewMode] = useState('grid');
  const { playFullQuran, state: audioState } = useAudio();

  useEffect(() => {
    const fetchSurahs = async () => {
      try {
        const response = await quranApi.getSurahs();
        setSurahs(response.data);
      } catch (error) {
        console.error('Error fetching surahs:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchSurahs();
  }, []);

  const filteredSurahs = surahs.filter(surah => {
    const matchesSearch = 
      surah.name_english.toLowerCase().includes(searchQuery.toLowerCase()) ||
      surah.name_arabic.includes(searchQuery) ||
      (surah.name_french && surah.name_french.toLowerCase().includes(searchQuery.toLowerCase())) ||
      surah.number.toString() === searchQuery;
    
    const matchesFilter = filter === 'all' || surah.revelation_place.toLowerCase() === filter;
    
    return matchesSearch && matchesFilter;
  });

  if (loading) return <PageLoading />;

  return (
    <div className="min-h-screen py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <SectionHeader
            overline={t('quran.list_overline')}
            title={t('quran.list_title')}
            subtitle={t('quran.list_subtitle')}
          />

          {/* Audio Coran complet */}
          <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 rounded-xl bg-primary/5 border border-primary/20">
            <div>
              <p className="text-sm font-medium text-text-primary">
                {t('quran.audio_full_quran_title')}
              </p>
              <p className="text-xs text-text-muted mt-0.5">
                {t('quran.audio_current_reciter')} : <span className="text-primary">{audioState.reciterId}</span>
                {' '}· {t('quran.audio_mode')} : {audioState.audioMode === 'offline_only' ? t('quran.audio_mode_offline') : t('quran.audio_mode_hybrid')}
              </p>
            </div>
            <div className="flex gap-2">
              <Link to="/settings/audio">
                <Button variant="outline" size="sm" data-testid="quran-list-audio-manage">
                  {t('quran.audio_manage')}
                </Button>
              </Link>
              <Button
                size="sm"
                onClick={() => playFullQuran()}
                className="bg-primary text-background hover:bg-primary/90"
                data-testid="quran-list-play-full"
              >
                <Play className="w-4 h-4 mr-1.5" />
                {t('quran.audio_play_full')}
              </Button>
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-4 mt-8">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text-muted" />
              <Input
                placeholder={t('quran.search_placeholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-surface border-border"
                data-testid="surah-search-input"
              />
            </div>
            
            <div className="flex gap-2">
              <Button
                variant={filter === 'all' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilter('all')}
                className={filter === 'all' ? 'bg-primary text-background' : 'border-border text-text-secondary'}
                data-testid="filter-all"
              >
                {t('quran.filter_all')}
              </Button>
              <Button
                variant={filter === 'meccan' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilter('meccan')}
                className={filter === 'meccan' ? 'bg-primary text-background' : 'border-border text-text-secondary'}
                data-testid="filter-meccan"
              >
                {t('quran.filter_meccan')}
              </Button>
              <Button
                variant={filter === 'medinan' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilter('medinan')}
                className={filter === 'medinan' ? 'bg-primary text-background' : 'border-border text-text-secondary'}
                data-testid="filter-medinan"
              >
                {t('quran.filter_medinan')}
              </Button>
            </div>

            <div className="flex gap-1 ml-auto">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setViewMode('grid')}
                className={viewMode === 'grid' ? 'text-primary' : 'text-text-muted'}
              >
                <Grid className="w-5 h-5" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setViewMode('list')}
                className={viewMode === 'list' ? 'text-primary' : 'text-text-muted'}
              >
                <List className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>

        {/* Surah Grid/List */}
        {viewMode === 'grid' ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredSurahs.map((surah, index) => (
              <Link 
                key={surah.number} 
                to={`/quran/${surah.number}`}
                data-testid={`surah-card-${surah.number}`}
              >
                <AnimatedCard 
                  index={index}
                  className="h-full hover:scale-[1.02] cursor-pointer"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <span className="text-primary font-serif text-lg">{surah.number}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-serif text-lg text-text-primary truncate">
                        {surah.name_french || surah.name_english}
                      </h3>
                      <p className="font-arabic text-primary text-xl" dir="rtl">
                        {surah.name_arabic}
                      </p>
                    </div>
                  </div>
                  
                  <div className="mt-4 flex items-center gap-4 text-sm text-text-muted">
                    <span className="flex items-center gap-1">
                      <FileText className="w-4 h-4" />
                      {surah.verses_count} {t('quran.verses_count', { count: surah.verses_count }).replace(`${surah.verses_count} `, '')}
                    </span>
                    <span className="flex items-center gap-1">
                      <MapPin className="w-4 h-4" />
                      {surah.revelation_place === 'Meccan' ? t('quran.place_meccan') : t('quran.place_medinan')}
                    </span>
                  </div>
                  
                  {surah.themes && surah.themes.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-1">
                      {surah.themes.slice(0, 3).map(theme => (
                        <span 
                          key={theme}
                          className="text-xs px-2 py-0.5 rounded bg-surface-elevated text-text-secondary"
                        >
                          {theme}
                        </span>
                      ))}
                    </div>
                  )}
                </AnimatedCard>
              </Link>
            ))}
          </div>
        ) : (
          <div className="space-y-2">
            {filteredSurahs.map((surah, index) => (
              <Link 
                key={surah.number} 
                to={`/quran/${surah.number}`}
                data-testid={`surah-row-${surah.number}`}
              >
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.02 }}
                  className="glass-card p-4 flex items-center gap-4 hover:border-primary/30 transition-all"
                >
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-primary font-serif">{surah.number}</span>
                  </div>
                  <div className="flex-1 min-w-0 flex items-center gap-4">
                    <h3 className="font-serif text-text-primary">{surah.name_french || surah.name_english}</h3>
                    <p className="font-arabic text-primary" dir="rtl">{surah.name_arabic}</p>
                  </div>
                  <div className="hidden sm:flex items-center gap-4 text-sm text-text-muted">
                    <span>{surah.verses_count} {t('quran.verses_count', { count: surah.verses_count }).replace(`${surah.verses_count} `, '')}</span>
                    <span className="px-2 py-0.5 rounded bg-surface-elevated">
                      {surah.revelation_place === 'Meccan' ? t('quran.place_meccan') : t('quran.place_medinan')}
                    </span>
                  </div>
                  <BookOpen className="w-5 h-5 text-text-muted" />
                </motion.div>
              </Link>
            ))}
          </div>
        )}

        {filteredSurahs.length === 0 && (
          <div className="text-center py-16">
            <BookOpen className="w-16 h-16 text-text-muted mx-auto mb-4" />
            <h3 className="text-lg font-medium text-text-primary mb-2">{t('quran.no_surah_found')}</h3>
            <p className="text-text-secondary">{t('quran.try_other_search')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default QuranList;
